import {
    ChatBubbleBottomCenterTextIcon,
    ChatBubbleLeftRightIcon,
    ClipboardDocumentCheckIcon,
    ChartBarIcon,
    CreditCardIcon,
    ShoppingBagIcon,
    HomeModernIcon,
    LifebuoyIcon,
    StarIcon,
    TrophyIcon,
    WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

import { porteLesCommandes } from '../../domain/ecranCommandes';
import { messagerieMenageOuverte } from '../../domain/menage/messagerieMenage';
import type { Facility } from '../../pages/settings/facilities/useFacility';

import { t } from '../i18n/langue';

/**
 * Le registre d'écrans du mobile — décision #17.
 *
 * **Une seule liste gouverne tout** : ce qui s'affiche au panneau, ce qui s'ouvre
 * quand on tape une URL, et où l'on atterrit après connexion. Le mobile n'invente
 * aucun test d'accès ; il applique les tests existants (`hasPermission`, les flags
 * `admin_has_*` de l'établissement) à sa propre liste.
 *
 * C'est le point où le mobile s'écarte volontairement du web. Là-bas les deux axes
 * vivent à deux endroits sans rien qui les relie — la permission déclarative dans
 * `config/routePermissions.ts`, le module inline dans `DefaultLayout.tsx:241` — et
 * le défaut est visible : `defaultRouteOrder` contient `/loyalty` **sans** son flag
 * de module, donc `SmartRedirect` peut poser sur une page de fidélité coupée un
 * utilisateur qui a pourtant `loyalty.view`. Avec une liste unique, « visible »,
 * « atteignable en tapant l'URL » et « choisi comme écran d'accueil » sont par
 * construction la même réponse.
 *
 * Ce qui n'est PAS ici, et pourquoi :
 *
 * - **`/diagnostic`** — la coque du lot 0. Joignable en tapant l'URL, jamais listé :
 *   au registre, il compterait dans les seuils 0/1/≥2 et le comportement « un seul
 *   écran » ne serait plus testable sur un vrai téléphone. #80 lui succède.
 * - **« Commandes » (#87) et l'accès aux conversations fermées (#30)** — leurs
 *   écrans n'existent pas encore, et une entrée de menu qui ne mène nulle part casse
 *   sa promesse à chaque tap (#17 §7). Ils s'ajouteront d'une ligne.
 */

export type IconeEcran = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Les seules clés de `Facility` qui portent l'activation d'un module.
 *
 * Écrites à la main, et pas dérivées de `keyof Facility` : ce type porte
 * `[key: string]: any` (`useFacility.ts:115`), donc `keyof Facility` vaut
 * `string | number` et tout `Extract` s'y effondre en `never`. L'index signature
 * fait d'ailleurs que `facility[module]` est typé `any` — d'où le `=== true`
 * explicite dans `estEcranEligible`, seul rempart contre un `admin_has_*` qui
 * arriverait à `1` ou `"1"` depuis l'API plutôt qu'à `true`.
 *
 * Le lien avec `Facility` est donc tenu par le test de `mobileScreens.test.tsx`,
 * qui vérifie que chaque flag déclaré ici existe bien dans une charge utile réelle.
 */
export type FlagModule =
    | 'admin_has_housekeeping'
    | 'housekeeping_messaging_enabled'
    | 'admin_has_loyalty'
    | 'admin_has_reviews'
    | 'admin_has_payment';

export interface EcranMobile {
    /** Identifiant stable, indépendant du chemin. */
    cle: string;
    chemin: string;
    /** Lu au rendu (getter du registre) : il suit la langue de l'app. */
    readonly libelle: string;
    icone: IconeEcran;
    /** Axe 1 — le droit. Testé par `hasPermission`, bypass compris. */
    permission: string;
    /**
     * Axe 2 — le fait. Absent quand le module n'existe pas : messagerie et
     * dashboard n'ont AUCUN flag, l'axe module ne mord donc que sur les écrans
     * non portés du lot 1.
     */
    module?: FlagModule;
    /**
     * L'écran existe-t-il au mobile ? Les non portés ne deviennent pas invisibles :
     * ils alimentent les cartes de repli du dashboard (#87), sous exactement le même
     * test d'éligibilité — sans quoi une carte « Fidélité — consultable sur
     * ordinateur » apprendrait à un agent d'entretien à la fois la configuration de
     * l'hôtel et l'existence de ce qu'il n'a pas le droit de voir.
     */
    porte: boolean;
    ordre: number;
    /**
     * Axe 3 — réservé à l'équipe TriggerFlow (`is_super_admin`). Pas une fausse
     * permission : `hasPermission` est vrai pour tout admin d'hôtel (contournement
     * `isAdmin`), il ne peut donc pas dire « super admin ».
     */
    superAdminSeulement?: boolean;
}

/**
 * L'atterrissage vise la messagerie d'abord.
 *
 * Pas d'alignement sur `defaultRouteOrder` du web, qui démarre par `/dashboard` :
 * la messagerie est la raison d'être de l'app, et ouvrir sur un dashboard en
 * lecture seule ferait payer un tap à chaque session pour arriver là où on allait.
 */
export const CLE_MESSAGERIE = 'messagerie';

/** L'écran dont l'éligibilité ne se lit ni sur une permission ni sur un module. */
export const CLE_COMMANDES = 'commandes';

/**
 * « Mes chambres » — lot 3 Odomo (09/10).
 *
 * Le second atterrissage : un compte sans messagerie mais avec le ménage (l'agent
 * d'étage, et par arbitrage la gouvernante) ouvre l'app sur ses chambres, pas sur
 * un tableau de bord. C'est aussi le seul autre écran pour lequel un push existe
 * (`mobile_path: '/housekeeping'`), d'où l'amorce élargie.
 */
export const CLE_MENAGE = 'housekeeping';

/**
 * « Maintenance » — l'écran du technicien (Beaucour, 13/10).
 *
 * Les pannes signalées par les femmes de chambre : à prendre, mes interventions,
 * terminées. Même module que « Mes chambres » ; la permission est celle de la page
 * web `/housekeeping/maintenance`. C'est l'atterrissage du technicien (voir
 * `useEcrans`) et la cible des pushes de panne (`mobile_path: '/maintenance/{id}'`).
 */
export const CLE_MAINTENANCE = 'maintenance';

/**
 * Ce qui distingue le technicien de la gouvernante, à permissions égales sur la
 * maintenance : il n'affecte pas les chambres. Le web teste le rôle
 * `housekeeping-technician` (SmartRedirect) ; le mobile reste sur les permissions,
 * comme tout son registre — un rôle personnalisé « technicien » sans ce slug
 * atterrit donc aussi sur Maintenance.
 */
export const PERMISSION_AFFECTER_MENAGE = 'housekeeping.assign';

/**
 * « Contrôles » — la tournée de la gouvernante (Beaucour, 14/10) : valider ou
 * refuser les chambres de tout l'hôtel. Même droit que les routes de contrôle.
 */
export const CLE_CONTROLES = 'controles';

/**
 * « Support » — l'équipe TriggerFlow traite les conversations de l'assistant depuis
 * l'app (spec 2026-10-17). Super admins seulement, quel que soit l'établissement
 * sélectionné (les endpoints `/super-admin/support` ne lisent pas `X-Facility-Id`).
 * C'est leur atterrissage, et la cible des pushes support (`/support/{id}`).
 */
export const CLE_SUPPORT = 'support';

/** La messagerie interne du ménage — pas celle des clients (`CLE_MESSAGERIE`). */
export const CLE_MESSAGES_MENAGE = 'hk-messages';

export const ECRANS_MOBILES: readonly EcranMobile[] = [
    {
        cle: CLE_SUPPORT,
        chemin: '/support',
        get libelle() {
            return t('nav.support');
        },
        icone: LifebuoyIcon,
        // Toujours vraie pour un super admin ; la vraie restriction est l'axe 3.
        permission: 'messaging.view',
        superAdminSeulement: true,
        porte: true,
        ordre: 0,
    },
    {
        cle: CLE_MESSAGERIE,
        chemin: '/messaging',
        get libelle() {
            return t('nav.messagerie');
        },
        icone: ChatBubbleLeftRightIcon,
        permission: 'messaging.view',
        porte: true,
        ordre: 1,
    },
    {
        cle: 'dashboard',
        chemin: '/dashboard',
        get libelle() {
            return t('nav.tableauDeBord');
        },
        icone: ChartBarIcon,
        permission: 'dashboard.view',
        porte: true,
        ordre: 2,
    },

    {
        cle: 'commandes',
        chemin: '/orders',
        get libelle() {
            return t('nav.commandes');
        },
        icone: ShoppingBagIcon,
        // Pas de permission dédiée dans le codebase : la consultation des
        // commandes suit celle des produits, comme sur le web.
        permission: 'products.view',
        porte: true,
        ordre: 3,
    },

    {
        cle: CLE_MENAGE,
        chemin: '/housekeeping',
        get libelle() {
            return t('nav.mesChambres');
        },
        icone: HomeModernIcon,
        permission: 'housekeeping.view',
        module: 'admin_has_housekeeping',
        porte: true,
        ordre: 4,
    },

    {
        cle: CLE_MAINTENANCE,
        chemin: '/maintenance',
        get libelle() {
            return t('nav.maintenance');
        },
        icone: WrenchScrewdriverIcon,
        permission: 'housekeeping.maintenance.view',
        module: 'admin_has_housekeeping',
        porte: true,
        ordre: 5,
    },

    {
        cle: CLE_CONTROLES,
        chemin: '/controles',
        get libelle() {
            return t('nav.controles');
        },
        icone: ClipboardDocumentCheckIcon,
        permission: PERMISSION_AFFECTER_MENAGE,
        module: 'admin_has_housekeeping',
        porte: true,
        ordre: 6,
    },

    {
        // La messagerie INTERNE du ménage (Beaucour) : allumée hôtel par hôtel.
        cle: CLE_MESSAGES_MENAGE,
        chemin: '/hk-messages',
        get libelle() {
            return t('nav.messagesMenage');
        },
        icone: ChatBubbleBottomCenterTextIcon,
        permission: 'housekeeping.view',
        module: 'housekeeping_messaging_enabled',
        porte: true,
        ordre: 7,
    },

    // ── Non portés : repli générique, consommé par #87 ────────────────────────
    {
        cle: 'loyalty',
        chemin: '/loyalty',
        get libelle() {
            return t('nav.fidelite');
        },
        icone: TrophyIcon,
        permission: 'loyalty.view',
        module: 'admin_has_loyalty',
        porte: false,
        ordre: 11,
    },
    {
        cle: 'reviews',
        chemin: '/reviews',
        get libelle() {
            return t('nav.avis');
        },
        icone: StarIcon,
        permission: 'reviews.view',
        module: 'admin_has_reviews',
        porte: false,
        ordre: 12,
    },
    {
        cle: 'payments',
        chemin: '/payments',
        get libelle() {
            return t('nav.paiements');
        },
        icone: CreditCardIcon,
        permission: 'payments.view',
        module: 'admin_has_payment',
        porte: false,
        ordre: 13,
    },
];

/**
 * Un écran est-il éligible pour ce compte, sur cet établissement ?
 *
 * Deux axes, et l'asymétrie du web est conservée : **le bypass joue sur la
 * permission seule, jamais sur le module** (#17 §5). Il n'y a rien à écrire pour
 * l'obtenir — `hasPermission` porte déjà `isSuperAdmin || isAdmin || '*'`. L'axe
 * module, lui, n'est pas un droit : c'est un fait sur l'établissement, et un
 * super-admin sur un hôtel sans gouvernante ne voit pas la gouvernante parce qu'il
 * n'y a rien à voir.
 */
export function estEcranEligible(
    ecran: EcranMobile,
    hasPermission: (permission: string) => boolean,
    isSuperAdmin: boolean,
    facility: Facility | null,
    nombreDeCatalogues?: number,
): boolean {
    if (ecran.superAdminSeulement && !isSuperAdmin) return false;
    if (!hasPermission(ecran.permission)) return false;

    /*
     * Troisième axe, propre aux Commandes (#40, #87) : « au moins un catalogue ».
     *
     * Il n'existe pas d'`admin_has_orders`, donc pas de flag de module à lire. Le
     * prédicat vit dans `domain/ecranCommandes.ts` parce qu'il gouverne AUSSI le
     * module `orders` du dashboard — une seule règle, deux surfaces (#40). L'écrire
     * ici en plus garantirait qu'elles divergent.
     *
     * Il mesure l'ÉQUIPEMENT et non l'historique : un hôtel qui vient de configurer
     * son premier catalogue n'a aucune commande, et doit quand même voir l'écran.
     */
    if (ecran.cle === CLE_COMMANDES) return porteLesCommandes(nombreDeCatalogues);

    // La messagerie du ménage exige le module ménage ET son interrupteur.
    if (ecran.cle === CLE_MESSAGES_MENAGE) return messagerieMenageOuverte(facility);

    if (!ecran.module) return true;

    // `null` — établissement pas encore connu — n'est pas « module coupé » : la
    // coque ne rend rien tant qu'on en est là (#27). Le test reste sûr quand même.
    return facility?.[ecran.module] === true;
}
